import { useTranslate } from "@tolgee/react";
import React from "react";
import {
  nonEmptyName,
  useChangeLogLabels,
} from "../../change-log/changeLogLabels.js";
import type { ChangeLogEntityStrategy } from "../../change-log/changeLogTypes.js";

/**
 * Entity-render strategy for the quote change log (design D7). Keeps its two HOME-kind
 * formats — the only kinds the quote log's scope ever surfaces (target-naming D2's
 * quote-log exemption: visually unchanged for everything it shows today) — and delegates
 * every other kind to the shared kind-qualified display rule, defensively, since no other
 * kind is expected here:
 * - `DOCUMENT` (the quote header) → a localised "Quote", not the bare number
 *   snapshot, so document-level changes (discount, binding, VAT, …) read clearly.
 * - `ITEM` → `<position> <name>` (a plain space, matching the app's `pathForPdf title`
 *   convention), unqualified, deep-linked to the item editor when live; deleted items and
 *   the document header render label-only. The name prefers the action's `nameOverride`
 *   (the name as of the action, design D2, empty/whitespace counting as no name — design
 *   D7), then the resolved `entity.name`. Once the server started returning `null` on a
 *   resolution miss instead of the raw id (target-naming D3), a THIRD fallback to
 *   `entity.entityId` would leak that id — so a name-resolution miss falls through to the
 *   shared rule (`entityDisplayLabel`) instead, which renders the kind label alone ("Item",
 *   "Item (deleted)") — never the raw id, on this surface either (target-naming D2).
 * - anything else → `entityDisplayLabel` (target-naming D2), kind-qualified like the
 *   project log — a quote's document scope never contains a requirement, project-child or
 *   other-aggregate entry, so this branch is a safety net rather than a shown format.
 */
export function useQuoteEntityStrategy(
  pathToDoc: string
): ChangeLogEntityStrategy {
  const { t } = useTranslate(["Quote"]);
  const { entityDisplayLabel } = useChangeLogLabels();
  return React.useMemo(
    () => ({
      entityLabel: (entity, nameOverride) => {
        switch (entity.entityType) {
          case "DOCUMENT":
            return t("Quote", { ns: "Quote" });
          case "ITEM": {
            const name =
              nonEmptyName(nameOverride) ?? nonEmptyName(entity.name);
            if (name === null) return entityDisplayLabel(entity, nameOverride);
            return entity.position ? `${entity.position} ${name}` : name;
          }
          default:
            return entityDisplayLabel(entity, nameOverride);
        }
      },
      entityLink: entity =>
        entity.entityType === "ITEM" && !entity.isDeleted
          ? `${pathToDoc}/edit/items/${entity.entityId}`
          : undefined,
      aggregateLabel: () => t("Quote", { ns: "Quote" }),
    }),
    [t, pathToDoc, entityDisplayLabel]
  );
}
